<template>
  <div class="d-flex flex-column gap-30 p-t-25 bg-gray-10 flex-1">
    <div class="d-flex flex-column gap-10">
      <bb-functional-accordion open>
        <template #header="{ expanded }">
          <div class="d-flex justify-content-between f-color-navy">
            <h4 class="f-gotham-medium">{{ translations.title }}</h4>
            <bb-icon
              :rotate="expanded ? 'down' : 'up'"
              name="ui-chevron-down"
              size="24"
            />
          </div>
        </template>
        <div class="d-flex flex-column gap-10 p-t-10" :data-testid="tableTestId">
          <bb-box
            data-testid="pending-row"
            v-for="row in tableRows"
            :key="`row-${row.id}`"
            class="bg-white border-radius-sm border-width-1 p-0"
            :class="checkboxStates[row.id] ? 'border-mint' : 'border-gray-30'"
          >
            <bb-functional-accordion>
              <template #header>
                <div class="d-flex align-items-center gap-10 p-x-10 p-y-15">
                  <bb-checkbox
                    v-model="checkboxStates[row.id]"
                    data-testid="pending-row-selection"
                    :input-attrs="{ 'aria-label': translations.row.selection }"
                    :name="`check_row_${1}`"
                    :title="translations.row.selection"
                    no-padding
                    class="d-flex align-items-center"
                    @click.native.stop
                  />
                  <div class="d-flex flex-column">
                    <span class="f-smallest f-gotham-bold f-color-gray">
                      {{ row.beneficiary[0] }}
                    </span>
                    <span class="f-tiny f-gotham-medium f-color-gray-70">
                      {{ row.beneficiary[1] }}
                    </span>
                  </div>
                  <div class="d-flex flex-column m-l-a f-align-right f-gotham-bold">
                    <span class="f-smallest f-color-gray">
                      {{ row.formattedAmount }}
                    </span>
                    <span
                      class="f-tiny"
                      :class="getPaymentStatusColor(row.status)"
                    >
                      {{ translations.status[row.status[0]] || row.status[0] }}
                    </span>
                  </div>
                </div>
              </template>
              <div class="d-flex flex-column gap-10 p-x-10 p-b-10">
                <div class="f-tiny f-color-gray d-flex flex-column">
                  <span class="f-gotham-bold">
                    {{ columnLabels.description }}
                  </span>
                  <span class="f-gotham-book">
                    {{ row.description }}
                  </span>
                </div>
                <div class="f-tiny f-color-gray d-flex flex-column">
                  <span class="f-gotham-bold">
                    {{ columnLabels.referenceNumber }}
                  </span>
                  <span class="f-gotham-book">
                    {{ row.referenceNumber }}
                  </span>
                </div>
                <div class="f-tiny f-color-gray d-flex flex-column">
                  <span class="f-gotham-bold">
                    {{ columnLabels.dateTime[0] }}
                  </span>
                  <span class="f-gotham-book">
                    {{ row.dateTime[0] }}
                  </span>
                </div>
                <div class="d-flex justify-content-center gap-15">
                  <bb-button
                    data-testid="pending-edit-button"
                    :label="translations.button.edit"
                    size="sm"
                    color="gray"
                    display="block"
                    inverted
                    @click.stop="onEdit(row.id)"
                  />
                  <bb-button
                    data-testid="pending-delete-button"
                    :label="translations.button.delete"
                    size="sm"
                    color="red"
                    display="block"
                    @click.stop="onDelete(row.id)"
                  />
                </div>
              </div>
            </bb-functional-accordion>
          </bb-box>
        </div>
      </bb-functional-accordion>
      <bb-box class="d-flex flex-column gap-10 bg-white p-x-10 border-radius-sm border-width-1 border-gray-30">
        <div class="d-flex justify-content-between f-gotham-bold f-color-gray">
          <span class="f-small">
            {{ translations.footer.totalAmount }}
          </span>
          <span class="f-smallest">
            {{ sumAmount }}
          </span>
        </div>
        <bb-button
          data-testid="pending-confirm-button"
          :disabled="!hasTruthyCheckboxes"
          :label="translations.button.confirm"
          size="sm"
        />
        <bb-button
          data-testid="pending-cancel-button"
          :disabled="!hasTruthyCheckboxes"
          :label="translations.button.cancelSelected"
          size="sm"
          color="gray"
          inverted
        />
      </bb-box>
    </div>
  </div>
</template>

<script>
import { OrderStatus } from '@bigbank/dc-common/clients/http/account/modules/pending-payments/pending-payment.types'
import pendingTableMixin from '@account/views/payments/pending/pending-table.mixin'
import { formatMoneyWithCurrency } from '@/plugins/numformat'
import { formatDate } from '@/plugins/dateFormatters'

export default {
  name: 'pending-payment-table-mobile',
  mixins: [pendingTableMixin],
  computed: {
    columnLabels () {
      return {
        dateTime: [this.translations.column.orderDate],
        referenceNumber: this.translations.column.referenceNumber,
        status: [this.translations.column.orderStatus],
        description: this.translations.column.description
      }
    },
    tableRows () {
      return this.pendingPayments.map(pending => ({
        id: pending.id,
        fromIban: pending.fromIban,
        dateTime: [formatDate(pending.orderDate)],
        status: [pending.orderStatus !== OrderStatus.NotConfirmed ? pending.orderStatus : null],
        beneficiary: [
          pending.recipientName,
          pending.recipientIban
        ],
        description: pending.description,
        referenceNumber: pending.referenceNumber,
        descriptionAndReference: [pending.description, pending.referenceNumber],
        amount: pending.amount,
        formattedAmount: formatMoneyWithCurrency(pending.amount, pending.currencyCode, this.locale)
      }))
    }
  }
}
</script>
